/*
 * Hosterr's own icon set: 24px grid, 1.75 stroke, round caps.
 * Icons are decorative (aria-hidden) unless given a `title`.
 */
import type { ReactNode, SVGProps } from "react";

export type IconProps = SVGProps<SVGSVGElement> & { size?: number; title?: string };

function make(name: string, body: ReactNode) {
  function Icon({ size = 18, title, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden={title ? undefined : true}
        role={title ? "img" : undefined}
        focusable="false"
        {...rest}
      >
        {title ? <title>{title}</title> : null}
        {body}
      </svg>
    );
  }
  Icon.displayName = `Icon${name}`;
  return Icon;
}

export const IconOverview = make(
  "Overview",
  <>
    <rect x="3.5" y="3.5" width="7" height="8" rx="1.5" />
    <rect x="13.5" y="3.5" width="7" height="5" rx="1.5" />
    <rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />
    <rect x="3.5" y="14.5" width="7" height="6" rx="1.5" />
  </>,
);

export const IconSites = make(
  "Sites",
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5c2.3 2.4 3.5 5.2 3.5 8.5s-1.2 6.1-3.5 8.5c-2.3-2.4-3.5-5.2-3.5-8.5s1.2-6.1 3.5-8.5Z" />
  </>,
);

export const IconServers = make(
  "Servers",
  <>
    <rect x="3.5" y="4" width="17" height="7" rx="1.75" />
    <rect x="3.5" y="13" width="17" height="7" rx="1.75" />
    <path d="M7.5 7.5h.01M7.5 16.5h.01" strokeWidth={2.4} />
    <path d="M11.5 7.5h5M11.5 16.5h5" />
  </>,
);

export const IconActivity = make(
  "Activity",
  <>
    <path d="M3.5 12h3.5l2.5-6 5 12 2.5-6h3.5" />
  </>,
);

export const IconSettings = make(
  "Settings",
  <>
    <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
    <circle cx="15" cy="7" r="2" />
    <circle cx="9" cy="17" r="2" />
  </>,
);

export const IconSearch = make(
  "Search",
  <>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </>,
);

export const IconPlus = make("Plus", <path d="M12 5v14M5 12h14" />);
export const IconMinus = make("Minus", <path d="M5 12h14" />);
export const IconCheck = make("Check", <path d="m5 12.5 4.5 4.5L19 7.5" />);
export const IconX = make("X", <path d="M6 6l12 12M18 6 6 18" />);
export const IconChevronDown = make("ChevronDown", <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />);
export const IconChevronRight = make("ChevronRight", <path d="m9.5 6.5 5.5 5.5-5.5 5.5" />);
export const IconChevronLeft = make("ChevronLeft", <path d="m14.5 6.5-5.5 5.5 5.5 5.5" />);
export const IconChevronsUpDown = make("ChevronsUpDown", <path d="m8 9.5 4-4 4 4M8 14.5l4 4 4-4" />);
export const IconArrowRight = make("ArrowRight", <path d="M5 12h14M13.5 6.5 19 12l-5.5 5.5" />);
export const IconArrowLeft = make("ArrowLeft", <path d="M19 12H5M10.5 6.5 5 12l5.5 5.5" />);
export const IconArrowDown = make("ArrowDown", <path d="M12 5v14M6.5 13.5 12 19l5.5-5.5" />);

export const IconCopy = make(
  "Copy",
  <>
    <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
    <path d="M15.5 8.5V6a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5" />
  </>,
);

export const IconExternal = make(
  "External",
  <>
    <path d="M13.5 4.5h6v6" />
    <path d="M19.5 4.5 11 13" />
    <path d="M18 14v4a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 5 18V7.5A1.5 1.5 0 0 1 6.5 6H10" />
  </>,
);

export const IconMenu = make("Menu", <path d="M4 7h16M4 12h16M4 17h16" />);

export const IconSidebar = make(
  "Sidebar",
  <>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M9.5 4.5v15" />
  </>,
);

export const IconLogOut = make(
  "LogOut",
  <>
    <path d="M14 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5H14" />
    <path d="M10 12h10M16.5 8.5 20 12l-3.5 3.5" />
  </>,
);

export const IconUser = make(
  "User",
  <>
    <circle cx="12" cy="8.5" r="3.75" />
    <path d="M4.5 20c.9-3.5 3.9-5.5 7.5-5.5s6.6 2 7.5 5.5" />
  </>,
);

export const IconUsers = make(
  "Users",
  <>
    <circle cx="9" cy="8.5" r="3.25" />
    <path d="M3 19.5c.7-3 3.1-4.75 6-4.75s5.3 1.75 6 4.75" />
    <path d="M15.5 5.6a3.25 3.25 0 0 1 0 5.8M17.5 14.9c1.8.6 3 2.2 3.5 4.6" />
  </>,
);

export const IconBuilding = make(
  "Building",
  <>
    <rect x="5" y="3.5" width="14" height="17" rx="1.5" />
    <path d="M9 7.5h1.5M13.5 7.5H15M9 11h1.5M13.5 11H15M9 14.5h1.5M13.5 14.5H15M10.5 20.5v-2.5h3v2.5" />
  </>,
);

export const IconSun = make(
  "Sun",
  <>
    <circle cx="12" cy="12" r="3.75" />
    <path d="M12 2.75v2M12 19.25v2M2.75 12h2M19.25 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M5.5 18.5l1.4-1.4M17.1 6.9l1.4-1.4" />
  </>,
);

export const IconMoon = make("Moon", <path d="M19.5 14.5A7.5 7.5 0 0 1 9.5 4.5a7.5 7.5 0 1 0 10 10Z" />);

export const IconMonitor = make(
  "Monitor",
  <>
    <rect x="3.5" y="4.5" width="17" height="11.5" rx="1.75" />
    <path d="M9 20h6M12 16v4" />
  </>,
);

export const IconAlert = make(
  "Alert",
  <>
    <path d="M10.3 4.4 2.9 17.5A2 2 0 0 0 4.6 20.5h14.8a2 2 0 0 0 1.7-3L13.7 4.4a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9.5v4M12 17h.01" />
  </>,
);

export const IconAlertCircle = make(
  "AlertCircle",
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.75v5M12 16.25h.01" />
  </>,
);

export const IconInfo = make(
  "Info",
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5.25M12 7.75h.01" />
  </>,
);

export const IconCheckCircle = make(
  "CheckCircle",
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.25 12.25 2.5 2.5 5-5.25" />
  </>,
);

export const IconXCircle = make(
  "XCircle",
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m9.25 9.25 5.5 5.5M14.75 9.25l-5.5 5.5" />
  </>,
);

export const IconCircleDashed = make(
  "CircleDashed",
  <>
    <circle cx="12" cy="12" r="8.5" strokeDasharray="3 3.1" />
  </>,
);

export const IconClock = make(
  "Clock",
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </>,
);

export const IconLock = make(
  "Lock",
  <>
    <rect x="5" y="10.5" width="14" height="10" rx="2" />
    <path d="M8.25 10.5V8a3.75 3.75 0 0 1 7.5 0v2.5" />
  </>,
);

export const IconUnlock = make(
  "Unlock",
  <>
    <rect x="5" y="10.5" width="14" height="10" rx="2" />
    <path d="M8.25 10.5V8a3.75 3.75 0 0 1 7.2-1.5" />
  </>,
);

export const IconShield = make(
  "Shield",
  <>
    <path d="M12 3.5 5 6v5.5c0 4.3 2.9 7.7 7 9 4.1-1.3 7-4.7 7-9V6l-7-2.5Z" />
    <path d="m9.25 12 2 2 3.5-3.75" />
  </>,
);

export const IconRefresh = make(
  "Refresh",
  <>
    <path d="M19.5 12a7.5 7.5 0 0 1-13.1 5" />
    <path d="M4.5 12a7.5 7.5 0 0 1 13.1-5" />
    <path d="M17.75 3.5v3.75H14M6.25 20.5v-3.75H10" />
  </>,
);

// A heart drawn from two circles, with one short beat kept inside it, so neither line crosses the other at small sizes.
export const IconHeartPulse = make(
  "HeartPulse",
  <>
    <path d="M12 19 5.25 12.25A4.25 4.25 0 1 1 12 7.25a4.25 4.25 0 1 1 6.75 5Z" />
    <path d="M8.5 12.75h2l1-1.75 1.5 3 1-1.25h1.5" />
  </>,
);

export const IconTrash = make(
  "Trash",
  <>
    <path d="M4.5 7h15M10 4.5h4M6.5 7l.8 11.6a2 2 0 0 0 2 1.9h5.4a2 2 0 0 0 2-1.9L17.5 7" />
    <path d="M10 11v5.5M14 11v5.5" />
  </>,
);

export const IconUnplug = make(
  "Unplug",
  <>
    <path d="m19.5 4.5-3 3M4.5 19.5l3-3" />
    <path d="m14 10-2 2M10.5 6.5l7 7-2 2a3.5 3.5 0 0 1-5 0l-2-2a3.5 3.5 0 0 1 0-5l2-2Z" />
  </>,
);

export const IconTerminal = make(
  "Terminal",
  <>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="m7.5 9.5 3 2.5-3 2.5M12.5 15h4" />
  </>,
);

export const IconCpu = make(
  "Cpu",
  <>
    <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
    <rect x="9.5" y="9.5" width="5" height="5" rx=".5" />
    <path d="M9.5 3.5v3M14.5 3.5v3M9.5 17.5v3M14.5 17.5v3M3.5 9.5h3M3.5 14.5h3M17.5 9.5h3M17.5 14.5h3" />
  </>,
);

export const IconMemory = make(
  "Memory",
  <>
    <rect x="3.5" y="7" width="17" height="10" rx="1.5" />
    <path d="M7.5 10.5v3M11 10.5v3M14.5 10.5v3M7 17v2.5M12 17v2.5M17 17v2.5" />
  </>,
);

export const IconDisk = make(
  "Disk",
  <>
    <ellipse cx="12" cy="6.5" rx="7.5" ry="3" />
    <path d="M4.5 6.5v11c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-11" />
    <path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
  </>,
);

export const IconGauge = make(
  "Gauge",
  <>
    <path d="M4.2 17.5a8.5 8.5 0 1 1 15.6 0" />
    <path d="m12 13 3.5-4" />
    <circle cx="12" cy="13.5" r="1.25" />
  </>,
);

export const IconKeyboard = make(
  "Keyboard",
  <>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <path d="M6 9.5h.01M9.5 9.5h.01M13 9.5h.01M16.5 9.5h.01M6.5 13h.01M17.5 13h.01M9 15h6" strokeWidth={2} />
  </>,
);

export const IconDownload = make(
  "Download",
  <>
    <path d="M12 4v11M7 10.5l5 5 5-5" />
    <path d="M4.5 16.5v1.75A1.75 1.75 0 0 0 6.25 20h11.5a1.75 1.75 0 0 0 1.75-1.75V16.5" />
  </>,
);

// Level, with a round bow and two square teeth: it still reads as a key at 14-18px, where a thin diagonal one broke up.
export const IconKey = make(
  "Key",
  <>
    <circle cx="7" cy="12" r="4.25" />
    <path d="M11.25 12h9.5v3.25M17 12v2.5" />
  </>,
);

export const IconMore = make("More", <path d="M6 12h.01M12 12h.01M18 12h.01" strokeWidth={2.75} />);

export const IconLink = make(
  "Link",
  <>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1-1" />
  </>,
);

export const IconWordPress = make(
  "WordPress",
  <>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M3.5 8.5h17" />
    <path d="M7 12h5M7 15.5h8" />
  </>,
);

export const IconFileCode = make(
  "FileCode",
  <>
    <path d="M14 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8L14 3.5Z" />
    <path d="M14 3.5V8h4.5" />
    <path d="m10 12-2 2 2 2M14 12l2 2-2 2" />
  </>,
);

export const IconCloud = make(
  "Cloud",
  <path d="M7 18.5a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9a4.75 4.75 0 0 1-.5 9.5H7Z" />,
);

export const IconPlug = make(
  "Plug",
  <>
    <path d="M9 3.5v4M15 3.5v4" />
    <path d="M6.5 7.5h11V11a5.5 5.5 0 0 1-11 0V7.5Z" />
    <path d="M12 16.5v4" />
  </>,
);

export const IconMapPin = make(
  "MapPin",
  <>
    <path d="M12 20.5s6.5-5.4 6.5-10.5a6.5 6.5 0 0 0-13 0c0 5.1 6.5 10.5 6.5 10.5Z" />
    <circle cx="12" cy="10" r="2.25" />
  </>,
);

export const IconCommand = make(
  "Command",
  <path d="M9 6.5A2.5 2.5 0 1 0 6.5 9H9V6.5Zm0 0v11m0-11h6m-6 11A2.5 2.5 0 1 1 6.5 15H9v2.5Zm0 0h6m0-11A2.5 2.5 0 1 1 17.5 9H15V6.5Zm0 0v11m0 0A2.5 2.5 0 1 0 17.5 15H15v2.5Z" />,
);

export const IconEye = make(
  "Eye",
  <>
    <path d="M2.75 12S6 5.5 12 5.5 21.25 12 21.25 12 18 18.5 12 18.5 2.75 12 2.75 12Z" />
    <circle cx="12" cy="12" r="2.75" />
  </>,
);

export const IconEyeOff = make(
  "EyeOff",
  <>
    <path d="M4 4l16 16" />
    <path d="M9.9 5.8A9.5 9.5 0 0 1 12 5.5c6 0 9.25 6.5 9.25 6.5a16 16 0 0 1-2.6 3.4M6.6 6.9C4 8.6 2.75 12 2.75 12S6 18.5 12 18.5a9 9 0 0 0 4.4-1.1" />
    <path d="M10 10.1a2.75 2.75 0 0 0 3.9 3.9" />
  </>,
);

export const IconGitBranch = make(
  "GitBranch",
  <>
    <circle cx="6.5" cy="5.5" r="2" />
    <circle cx="6.5" cy="18.5" r="2" />
    <circle cx="17.5" cy="7.5" r="2" />
    <path d="M6.5 7.5v9M17.5 9.5c0 4-4 4.5-9.2 7.6" />
  </>,
);

export const IconGitCommit = make(
  "GitCommit",
  <>
    <circle cx="12" cy="12" r="3.5" />
    <path d="M3.5 12h5M15.5 12h5" />
  </>,
);

export const IconRollback = make(
  "Rollback",
  <>
    <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3" />
    <path d="M4.5 4.5v3.75h3.75" />
    <path d="M12 8.5V12l2.5 1.5" />
  </>,
);

export const IconDeploy = make(
  "Deploy",
  <>
    <path d="M12 15.5V4.5M7.5 9 12 4.5 16.5 9" />
    <path d="M4.5 14.5v3.75A1.75 1.75 0 0 0 6.25 20h11.5a1.75 1.75 0 0 0 1.75-1.75V14.5" />
  </>,
);

export const IconLayers = make(
  "Layers",
  <>
    <path d="m12 3.5 8.5 4.5-8.5 4.5L3.5 8 12 3.5Z" />
    <path d="m3.5 12 8.5 4.5 8.5-4.5M3.5 16l8.5 4.5 8.5-4.5" />
  </>,
);

/** A lab flask, for the Test environment. */
export const IconFlask = make(
  "Flask",
  <>
    <path d="M9.5 3.75h5M10.25 3.75v5.1L5.4 17.2a2 2 0 0 0 1.73 3.05h9.74a2 2 0 0 0 1.73-3.05l-4.85-8.35v-5.1" />
    <path d="M7.4 14.25h9.2" />
  </>,
);

export const IconCode = make("Code", <path d="m8.5 7.5-4.5 4.5 4.5 4.5M15.5 7.5l4.5 4.5-4.5 4.5M13.5 5l-3 14" />);

export const IconWebhook = make(
  "Webhook",
  <>
    <path d="M9.5 8.2A3.5 3.5 0 1 1 15 11l-3 5.2" />
    <path d="M6.9 13.5A3.5 3.5 0 1 0 12 17.6h6" />
    <path d="M17 10.8a3.5 3.5 0 1 1-1.5 6.7" />
  </>,
);

export const IconEraser = make(
  "Eraser",
  <>
    <path d="m7 20.5-3.3-3.3a1.75 1.75 0 0 1 0-2.5l9.5-9.5a1.75 1.75 0 0 1 2.5 0l4.1 4.1a1.75 1.75 0 0 1 0 2.5L12.3 19.3" />
    <path d="M7 20.5h13M9 11.5l5.5 5.5" />
  </>,
);

export const IconPasskey = make(
  "Passkey",
  <>
    <circle cx="9" cy="8" r="3.75" />
    <path d="M2.75 20c.6-3.2 3.1-5.25 6.25-5.25 1.2 0 2.3.3 3.2.8" />
    <circle cx="17.5" cy="13.5" r="2.25" />
    <path d="M17.5 15.75v4.75M17.5 18.5h1.75" />
  </>,
);

export const IconArchive = make(
  "Archive",
  <>
    <rect x="3.5" y="4.5" width="17" height="4.5" rx="1.25" />
    <path d="M5 9v9.25c0 .7.55 1.25 1.25 1.25h11.5c.7 0 1.25-.55 1.25-1.25V9M10 13h4" />
  </>,
);

export const IconScan = make(
  "Scan",
  <>
    <path d="M4 8V5.75C4 4.78 4.78 4 5.75 4H8M16 4h2.25C19.22 4 20 4.78 20 5.75V8M20 16v2.25c0 .97-.78 1.75-1.75 1.75H16M8 20H5.75C4.78 20 4 19.22 4 18.25V16" />
    <path d="M7.5 12h9" />
  </>,
);

export const IconWall = make(
  "Wall",
  <>
    <rect x="3.5" y="5" width="17" height="14" rx="1.5" />
    <path d="M3.5 9.75h17M3.5 14.25h17M9 5v4.75M15 5v4.75M12 9.75v4.5M7 14.25V19M17 14.25V19" />
  </>,
);

export const IconPower = make(
  "Power",
  <>
    <path d="M12 3.5v8" />
    <path d="M7.25 6.5a7.25 7.25 0 1 0 9.5 0" />
  </>,
);

export const IconPackage = make(
  "Package",
  <>
    <path d="m12 3.5 8 4.25v8.5L12 20.5l-8-4.25v-8.5L12 3.5Z" />
    <path d="m4 7.75 8 4.25 8-4.25M12 12v8.5M8 5.6l8 4.3" />
  </>,
);

export const IconMail = make(
  "Mail",
  <>
    <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
    <path d="m4 7 8 6 8-6" />
  </>,
);

/* Milestone 6: domains, billing, clients, branding, notifications. */
export const IconDomain = make(
  "Domain",
  <>
    <rect x="3" y="6.5" width="18" height="11" rx="2.5" />
    {/* "ww", each as wide as it can be inside the frame, so the zigzags don't run together at 16-18 px. */}
    <path d="M5.5 10.25l1.4 3.5 1.35-2.6 1.35 2.6 1.4-3.5M12.99 10.25l1.4 3.5 1.35-2.6 1.35 2.6 1.4-3.5" />
  </>,
);

export const IconCard = make(
  "Card",
  <>
    <rect x="3" y="5.5" width="18" height="13" rx="2" />
    <path d="M3 10h18M7 14.5h3" />
  </>,
);

export const IconReceipt = make(
  "Receipt",
  <>
    <path d="M6 3.5h12v17l-2.5-1.5-2 1.5-1.5-1.5-1.5 1.5-2-1.5L6 20.5v-17Z" />
    <path d="M9 8h6M9 11.5h6M9 15h3.5" />
  </>,
);

export const IconBriefcase = make(
  "Briefcase",
  <>
    <rect x="3.5" y="7" width="17" height="12.5" rx="2" />
    <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3.5 12.5h17" />
  </>,
);

export const IconPalette = make(
  "Palette",
  <>
    <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.1 0 1.75-.75 1.75-1.6 0-.5-.25-.9-.5-1.25-.3-.35-.5-.75-.5-1.25 0-.95.8-1.65 1.75-1.65h2A3.5 3.5 0 0 0 20.5 11 7.9 7.9 0 0 0 12 3.5Z" />
    <path d="M7.5 11.5h.01M10 7.5h.01M14.5 7.5h.01M17 11h.01" strokeWidth={2.4} />
  </>,
);

export const IconBell = make(
  "Bell",
  <>
    <path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.5 2h-15l1.5-2Z" />
    <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
  </>,
);

export const IconHelp = make(
  "Help",
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.6 9.4a2.5 2.5 0 0 1 4.85.85c0 1.65-2.45 2.25-2.45 3.75" />
    <path d="M12 16.75h.01" strokeWidth={2.4} />
  </>,
);

export const IconMove = make("Move", <path d="M4 8h14.5M15 4.5 18.5 8 15 11.5M20 16H5.5M9 12.5 5.5 16 9 19.5" />);

export const IconSend = make("Send", <path d="M20.5 3.5 10 14M20.5 3.5 14 20.5l-4-6.5-6.5-4 17-6.5Z" />);

/* Lists: grid and list views, sorting, a section list. */
export const IconGrid = make(
  "Grid",
  <>
    <rect x="4" y="4" width="7" height="7" rx="1.5" />
    <rect x="13" y="4" width="7" height="7" rx="1.5" />
    <rect x="4" y="13" width="7" height="7" rx="1.5" />
    <rect x="13" y="13" width="7" height="7" rx="1.5" />
  </>,
);
export const IconList = make("List", <path d="M9 6.5h11M9 12h11M9 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />);
export const IconArrowUp = make("ArrowUp", <path d="M12 19V5M6.5 10.5 12 5l5.5 5.5" />);
export const IconChevronUp = make("ChevronUp", <path d="m6.5 14.5 5.5-5.5 5.5 5.5" />);
export const IconGrip = make("Grip", <path d="M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01" strokeWidth={2.6} />);
export const IconSliders = make("Sliders", <path d="M4 7h10M18 7h2M4 17h4M12 17h8M14 4.5v5M8 14.5v5" />);

/* An animated ring for work in progress. With reduced motion it stays still. */
export function Spinner({ size = 16, className = "" }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={`animate-spin-slow ${className}`}
    >
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeOpacity=".22" strokeWidth={2.25} />
      <path d="M20.5 12A8.5 8.5 0 0 0 12 3.5" stroke="currentColor" strokeWidth={2.25} strokeLinecap="round" />
    </svg>
  );
}

/** Quick actions: a bolt. */
export const IconZap = make("Zap", <path d="M13 3 5 13.5h6.5L10.5 21 19 10.5h-6.5L13 3Z" />);

/** Site tags. */
export const IconTag = make(
  "Tag",
  <>
    <path d="M3.75 12.6V4.75a1 1 0 0 1 1-1h7.85l7.65 7.65a1.5 1.5 0 0 1 0 2.1l-5.75 5.75a1.5 1.5 0 0 1-2.1 0L3.75 12.6Z" />
    <circle cx="8.25" cy="8.25" r="1.25" />
  </>,
);

/** A note left on something (a DNS record's note): a sheet with a folded corner. */
export const IconNote = make(
  "Note",
  <>
    <path d="M14.5 20.5H6A1.5 1.5 0 0 1 4.5 19V5A1.5 1.5 0 0 1 6 3.5h12A1.5 1.5 0 0 1 19.5 5v10.5l-5 5Z" />
    <path d="M14.5 20.5v-3.5a1.5 1.5 0 0 1 1.5-1.5h3.5" />
    <path d="M8.5 8.5h7M8.5 12h4.5" />
  </>,
);

/** Dates: a calendar page. */
export const IconCalendar = make(
  "Calendar",
  <>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </>,
);

/** Bots (Traffic): a robot's head with an antenna. */
export const IconBot = make(
  "Bot",
  <>
    <rect x="4.5" y="8" width="15" height="11.5" rx="3" />
    <path d="M12 8V4.5M10.5 4.5h3M2.5 12.5v3M21.5 12.5v3" />
    <circle cx="9.25" cy="13.25" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="14.75" cy="13.25" r="1.1" fill="currentColor" stroke="none" />
  </>,
);

/** A page that isn't there (Traffic's missing pages): a sheet with a cross. */
export const IconFileX = make(
  "FileX",
  <>
    <path d="M14 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8L14 3.5Z" />
    <path d="M14 3.5V8h4.5M10 12l4 4M14 12l-4 4" />
  </>,
);

/** A speech bubble: website feedback, a comment. */
export const IconComment = make(
  "Comment",
  <>
    <path d="M4.5 6A1.5 1.5 0 0 1 6 4.5h12A1.5 1.5 0 0 1 19.5 6v9a1.5 1.5 0 0 1-1.5 1.5h-7l-4.5 3.5v-3.5H6A1.5 1.5 0 0 1 4.5 15Z" />
    <path d="M8.5 9h7M8.5 12h4.5" />
  </>,
);

export const IconPencil = make(
  "Pencil",
  <>
    <path d="M4.5 19.5h4l10.3-10.3a2.83 2.83 0 0 0-4-4L4.5 15.5v4Z" />
    <path d="m13.5 6.5 4 4" />
  </>,
);
